/**
 * Логика Texas Hold'em: колода, оценка 7→5 (упрощённый evaluator).
 * Портировано из poker-lan (superchee0526), исходник:
 * https://github.com/superchee0526/poker-lan/blob/4fd6506e9758346d0bbf54bd99526a5bc28e599d/poker.js
 *
 * Объекты карт: { rank, suit, value } — rank в нотации '2'..'T','J','Q','K','A', suit — символ масти.
 */

const SUITS = ['♠', '♥', '♦', '♣'];
const RANKS = ['2', '3', '4', '5', '6', '7', '8', '9', 'T', 'J', 'Q', 'K', 'A'];

const RANK_VALUE = {
  '2': 2,
  '3': 3,
  '4': 4,
  '5': 5,
  '6': 6,
  '7': 7,
  '8': 8,
  '9': 9,
  T: 10,
  J: 11,
  Q: 12,
  K: 13,
  A: 14,
};

export class Deck {
  constructor() {
    this.cards = [];
    this.reset();
  }

  reset() {
    this.cards = [];
    for (const s of SUITS) {
      for (const r of RANKS) {
        this.cards.push({ rank: r, suit: s, value: RANK_VALUE[r] });
      }
    }
    this.shuffle();
  }

  shuffle() {
    for (let i = this.cards.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [this.cards[i], this.cards[j]] = [this.cards[j], this.cards[i]];
    }
  }

  /** Снять верхнюю карту с колоды (после shuffle — случайный порядок). */
  deal() {
    return this.cards.pop();
  }
}

/**
 * Упрощённая оценка: 2 карманные + до 5 общих → лучшие 5.
 * @param {{ rank: string, suit: string, value: number }[]} holeCards
 * @param {{ rank: string, suit: string, value: number }[]} communityCards
 * @returns {{ score: number, name: string, tieBreaker?: number[] }}
 */
export function evaluateHand(holeCards, communityCards) {
  const allCards = [...holeCards, ...communityCards];

  allCards.sort((a, b) => b.value - a.value);

  const getCounts = (cards) => {
    const counts = {};
    cards.forEach((c) => {
      counts[c.rank] = (counts[c.rank] || 0) + 1;
    });
    return counts;
  };

  const isFlush = (cards) => {
    const suits = {};
    cards.forEach((c) => {
      suits[c.suit] = (suits[c.suit] || []).concat(c);
    });
    for (const s of SUITS) {
      const arr = suits[s];
      if (arr && arr.length >= 5) {
        arr.sort((a, b) => b.value - a.value);
        return arr.slice(0, 5);
      }
    }
    return false;
  };

  const isStraight = (cards) => {
    const uniqueValues = [...new Set(cards.map((c) => c.value))];
    if (uniqueValues.includes(14)) uniqueValues.push(1);
    uniqueValues.sort((a, b) => b - a);

    let streak = [];
    for (const v of uniqueValues) {
      if (streak.length === 0 || streak[streak.length - 1] === v + 1) {
        streak.push(v);
      } else {
        streak = [v];
      }
      if (streak.length === 5) return streak[0];
    }
    return false;
  };

  const flushCards = isFlush(allCards);
  if (flushCards) {
    const straightFlushHigh = isStraight(flushCards);
    if (straightFlushHigh) {
      const isRoyal =
        straightFlushHigh === 14 &&
        flushCards.some((c) => c.value === 13) &&
        flushCards.some((c) => c.value === 12) &&
        flushCards.some((c) => c.value === 11) &&
        flushCards.some((c) => c.value === 10);
      if (isRoyal) return { score: 900 + 14, name: 'Royal Flush', tieBreaker: [14, 13, 12, 11, 10] };
      return { score: 900 + straightFlushHigh, name: 'Straight Flush', tieBreaker: [straightFlushHigh] };
    }
    return {
      score: 600 + flushCards[0].value,
      name: 'Flush',
      tieBreaker: flushCards.map((c) => c.value),
    };
  }

  const counts = getCounts(allCards);
  const keys = Object.keys(counts);
  const quads = keys.filter((k) => counts[k] === 4);
  const trips = keys.filter((k) => counts[k] === 3);
  const pairs = keys.filter((k) => counts[k] === 2);

  if (quads.length > 0) {
    const qRank = quads.reduce((a, b) => (RANK_VALUE[a] > RANK_VALUE[b] ? a : b));
    const qVal = RANK_VALUE[qRank];
    return { score: 800 + qVal, name: `Four of a Kind (${qRank}s)` };
  }

  if (trips.length > 0 && (trips.length >= 2 || pairs.length > 0)) {
    const tRank = trips.reduce((a, b) => (RANK_VALUE[a] > RANK_VALUE[b] ? a : b));
    const tVal = RANK_VALUE[tRank];
    const remaining = keys.filter((k) => k !== tRank);
    const pairCandidates = remaining.filter((k) => counts[k] >= 2);
    if (pairCandidates.length > 0) {
      const pRank = pairCandidates.reduce((a, b) => (RANK_VALUE[a] > RANK_VALUE[b] ? a : b));
      const pVal = RANK_VALUE[pRank];
      return { score: 700 + tVal * 15 + pVal, name: 'Full House', tieBreaker: [tVal, pVal] };
    }
  }

  const straightHigh = isStraight(allCards);
  if (straightHigh) {
    return { score: 500 + straightHigh, name: 'Straight', tieBreaker: [straightHigh] };
  }

  if (trips.length > 0) {
    const tRank = trips.reduce((a, b) => (RANK_VALUE[a] > RANK_VALUE[b] ? a : b));
    const tVal = RANK_VALUE[tRank];
    return { score: 400 + tVal, name: `Three of a Kind (${tRank}s)` };
  }

  if (pairs.length >= 2) {
    pairs.sort((a, b) => RANK_VALUE[b] - RANK_VALUE[a]);
    return { score: 300 + RANK_VALUE[pairs[0]], name: 'Two Pair', tieBreaker: pairs.map((r) => RANK_VALUE[r]) };
  }

  if (pairs.length === 1) {
    return { score: 200 + RANK_VALUE[pairs[0]], name: `Pair of ${pairs[0]}s` };
  }

  return {
    score: 100 + allCards[0].value,
    name: 'High Card',
    tieBreaker: allCards.slice(0, 5).map((c) => c.value),
  };
}

const SUIT_UNICODE_TO_KEY = {
  '♠': 'spades',
  '♥': 'hearts',
  '♦': 'diamonds',
  '♣': 'clubs',
};

/** Масти в нотации `poker-ts` → unicode для общего пайплайна с `evaluateHand` / `pokerCardToPlayingCard`. */
const TS_SUIT_TO_UNICODE = {
  spades: '♠',
  hearts: '♥',
  diamonds: '♦',
  clubs: '♣',
};

/**
 * Карта `poker-ts` → внутренний формат движка (rank + unicode suit + value).
 * @param {{ rank: string, suit: 'clubs'|'diamonds'|'hearts'|'spades' } | null | undefined} c
 */
export function pokerTsCardToEngineCard(c) {
  if (!c?.rank || !c.suit) return null;
  const suit = TS_SUIT_TO_UNICODE[c.suit];
  if (!suit) return null;
  return { rank: c.rank, suit, value: RANK_VALUE[c.rank] };
}

/**
 * Карта `poker-ts` → `PlayingCard` для UI (`PokerTableSurface`, `IndustrialSeat`).
 * @param {{ rank: string, suit: string } | null | undefined} c
 */
export function pokerTsCardToPlayingCard(c) {
  const e = pokerTsCardToEngineCard(c);
  if (!e) return null;
  return pokerCardToPlayingCard(e);
}

/**
 * Карта движка → формат `PlayingCard` / `PokerTableSurface` (suit: spades|hearts|…, rank с «10»).
 * @param {{ rank: string, suit: string, value?: number } | null | undefined} c
 * @returns {{ suit: string, rank: string } | null}
 */
export function pokerCardToPlayingCard(c) {
  if (!c || !c.rank || !c.suit) return null;
  const suit = SUIT_UNICODE_TO_KEY[c.suit];
  if (!suit) return null;
  return { suit, rank: c.rank === 'T' ? '10' : c.rank };
}

/** Пять слотов для стола: заполнить слева направо, остальное `null`. */
export function boardToCommunitySlots(board) {
  const slots = [null, null, null, null, null];
  (board || []).forEach((c, i) => {
    if (i < 5) slots[i] = pokerCardToPlayingCard(c);
  });
  return slots;
}
